/**
 * Storage Cleanup & Migration Service
 *
 * P0 Fix: Automatically detects and cleans stale/invalid storage data
 * Prevents blank screens caused by corrupted localStorage/IndexedDB
 */

const APP_VERSION_KEY = "coretrak.app.version";
const STORAGE_VERSION_KEY = "coretrak.storage.version";
const CURRENT_STORAGE_VERSION = 2; // Increment when storage schema changes
const CURRENT_APP_VERSION = import.meta.env.VITE_APP_VERSION || "1.0.0";

// Storage keys that should be preserved (user data)
// FIX: Protect recents keys from cleanup - they're client-side and should survive code pushes
const PRESERVED_KEYS = [
  "coretrak.recents", // Nutrition recents pattern (coretrak.recents.{userId})
  "coretrak.workout.recents", // Workout recents (exact key)
  "coretrak.favourites", // Legacy favourites (for migration)
  "coretrak.settings.glp1",
  "coretrak.settings.unitSystem",
];

// Storage keys that can be safely cleared (app state)
const CLEARABLE_KEYS_PATTERNS = [
  "coretrak.nutrition.",
  "coretrak.onboarding.",
  "coretrak.cache.",
  "coretrak.todaySnapshot",
];

interface StorageCheckResult {
  isValid: boolean;
  needsCleanup: boolean;
  needsMigration: boolean;
  errors: string[];
}

/**
 * Check if localStorage is valid and accessible
 */
function checkLocalStorage(): StorageCheckResult {
  const errors: string[] = [];
  let isValid = true;
  let needsCleanup = false;

  try {
    // Test localStorage access
    const testKey = "__storage_test__";
    localStorage.setItem(testKey, "test");
    localStorage.removeItem(testKey);

    // Check for corrupted data
    const keys = Object.keys(localStorage);
    for (const key of keys) {
      if (key.startsWith("coretrak.")) {
        try {
          const value = localStorage.getItem(key);
          if (value) {
            // Try to parse JSON values
            if (value.startsWith("{") || value.startsWith("[")) {
              JSON.parse(value);
            }
          }
        } catch (error) {
          console.warn(
            `[StorageCleanup] Corrupted localStorage key detected: ${key}`,
            error,
          );
          errors.push(`Corrupted key: ${key}`);
          needsCleanup = true;
        }
      }
    }
  } catch (error) {
    console.error("[StorageCleanup] localStorage check failed:", error);
    errors.push("localStorage access failed");
    isValid = false;
  }

  return {
    isValid,
    needsCleanup,
    needsMigration: false,
    errors,
  };
}

/**
 * Check if IndexedDB is valid and accessible
 */
async function checkIndexedDB(): Promise<StorageCheckResult> {
  const errors: string[] = [];
  let isValid = true;
  let needsCleanup = false;
  let needsMigration = false;

  try {
    // Check if IndexedDB is supported
    if (!("indexedDB" in window)) {
      return {
        isValid: false,
        needsCleanup: false,
        needsMigration: false,
        errors: ["IndexedDB not supported"],
      };
    }

    // Check CoreTrakOfflineQueue database
    try {
      const dbName = "CoreTrakOfflineQueue";
      const dbVersion = 1;

      // Try to open the database
      await new Promise<void>((resolve, reject) => {
        const request = indexedDB.open(dbName, dbVersion);

        request.onsuccess = () => {
          const db = request.result;
          // Check if object store exists
          if (!db.objectStoreNames.contains("pendingEntries")) {
            errors.push(
              "IndexedDB schema mismatch: missing pendingEntries store",
            );
            needsMigration = true;
          }
          db.close();
          resolve();
        };

        request.onerror = () => {
          errors.push(
            `IndexedDB open failed: ${request.error?.message || "Unknown error"}`,
          );
          needsCleanup = true;
          reject(request.error);
        };

        request.onupgradeneeded = () => {
          // Database needs upgrade - this is normal for version changes
          needsMigration = true;
        };

        request.onblocked = () => {
          errors.push("IndexedDB upgrade blocked");
          needsCleanup = true;
        };
      });
    } catch (error) {
      console.error("[StorageCleanup] IndexedDB check failed:", error);
      errors.push(
        `IndexedDB check error: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
      needsCleanup = true;
    }
  } catch (error) {
    console.error("[StorageCleanup] IndexedDB access failed:", error);
    errors.push("IndexedDB access failed");
    isValid = false;
  }

  return {
    isValid,
    needsCleanup,
    needsMigration,
    errors,
  };
}

/**
 * Clean corrupted localStorage data
 * BUG FIX v31: More aggressive cleanup - removes ALL corrupted data, even from preserved keys
 */
function cleanLocalStorage(preserveUserData = true, aggressive = false): void {
  const keysToRemove: string[] = [];
  let keys: string[] = [];

  try {
    keys = Object.keys(localStorage);
  } catch (error) {
    console.error(
      "[StorageCleanup] Failed to access localStorage keys:",
      error,
    );
    // BUG FIX v31: If we can't access keys, try to clear everything if aggressive
    if (aggressive) {
      try {
        console.warn(
          "[StorageCleanup] Aggressive mode: Clearing all localStorage due to access failure",
        );
        localStorage.clear();
        return;
      } catch (clearError) {
        console.error(
          "[StorageCleanup] Failed to clear localStorage:",
          clearError,
        );
      }
    }
    return;
  }

  for (const key of keys) {
    if (key.startsWith("coretrak.")) {
      // FIX: Check if key is a recents key (protected from cleanup)
      const isRecentsKey =
        key.startsWith("coretrak.recents.") ||
        key === "coretrak.workout.recents";

      // In aggressive mode, check ALL keys for corruption, even preserved ones
      // BUT: Never remove recents keys - they're client-side and should survive cleanup
      const shouldCheck =
        (aggressive ||
          !preserveUserData ||
          !PRESERVED_KEYS.some(
            (preserved) => key === preserved || key.startsWith(preserved),
          )) &&
        !isRecentsKey;

      if (shouldCheck) {
        try {
          const value = localStorage.getItem(key);
          if (value) {
            // Try to parse - if it fails, it's corrupted
            if (value.startsWith("{") || value.startsWith("[")) {
              try {
                JSON.parse(value);
              } catch {
                // Corrupted JSON - remove it (even if preserved)
                console.warn(
                  `[StorageCleanup] Corrupted JSON detected in key: ${key}`,
                );
                keysToRemove.push(key);
              }
            }
          }
        } catch (error) {
          // Can't read - remove it
          console.warn(
            `[StorageCleanup] Error reading key ${key}, marking for removal:`,
            error,
          );
          keysToRemove.push(key);
        }
      } else if (!aggressive && preserveUserData) {
        // In non-aggressive mode, still validate preserved keys but don't remove if corrupted
        // This allows us to detect issues without breaking user data
        try {
          const value = localStorage.getItem(key);
          if (value && (value.startsWith("{") || value.startsWith("["))) {
            try {
              JSON.parse(value);
            } catch {
              console.warn(
                `[StorageCleanup] Preserved key ${key} has corrupted JSON but keeping it (non-aggressive mode)`,
              );
            }
          }
        } catch (error) {
          console.warn(
            `[StorageCleanup] Error validating preserved key ${key}:`,
            error,
          );
        }
      }
    }
  }

  // Remove corrupted keys (with error handling)
  let removedCount = 0;
  for (const key of keysToRemove) {
    try {
      localStorage.removeItem(key);
      removedCount++;
      console.log(`[StorageCleanup] Removed corrupted key: ${key}`);
    } catch (error) {
      console.error(`[StorageCleanup] Failed to remove key ${key}:`, error);
      // Continue with other keys even if one fails
    }
  }

  console.log(
    `[StorageCleanup] Cleaned ${removedCount} of ${keysToRemove.length} corrupted localStorage keys`,
  );

  // BUG FIX v31: If aggressive mode and we found corruption, clear all clearable keys
  // FIX: Never clear recents keys even in aggressive mode
  if (aggressive && removedCount > 0) {
    console.log(
      "[StorageCleanup] Aggressive mode: Clearing all clearable keys after corruption detected",
    );
    for (const key of keys) {
      if (key.startsWith("coretrak.")) {
        // FIX: Protect recents keys from aggressive cleanup
        const isRecentsKey =
          key.startsWith("coretrak.recents.") ||
          key === "coretrak.workout.recents";
        if (isRecentsKey) {
          console.log(
            `[StorageCleanup] Preserving recents key from aggressive cleanup: ${key}`,
          );
          continue;
        }

        const isClearable = CLEARABLE_KEYS_PATTERNS.some((pattern) =>
          key.startsWith(pattern),
        );
        if (isClearable && !keysToRemove.includes(key)) {
          try {
            localStorage.removeItem(key);
            console.log(
              `[StorageCleanup] Aggressively removed clearable key: ${key}`,
            );
          } catch (error) {
            console.error(
              `[StorageCleanup] Failed to aggressively remove key ${key}:`,
              error,
            );
          }
        }
      }
    }
  }
}

/**
 * Clean and reset IndexedDB
 */
async function cleanIndexedDB(): Promise<void> {
  console.log("[StorageCleanup] Cleaning IndexedDB...");

  try {
    const dbName = "CoreTrakOfflineQueue";

    // Delete and recreate the database
    await new Promise<void>((resolve, reject) => {
      const deleteRequest = indexedDB.deleteDatabase(dbName);

      deleteRequest.onsuccess = () => {
        console.log("[StorageCleanup] IndexedDB deleted successfully");
        resolve();
      };

      deleteRequest.onerror = () => {
        console.error(
          "[StorageCleanup] Failed to delete IndexedDB:",
          deleteRequest.error,
        );
        reject(deleteRequest.error);
      };

      deleteRequest.onblocked = () => {
        console.warn(
          "[StorageCleanup] IndexedDB delete blocked - database may be in use",
        );
        // Wait a bit and try to resolve anyway
        setTimeout(() => resolve(), 1000);
      };
    });

    // Recreate the database with correct schema
    await new Promise<void>((resolve, reject) => {
      const openRequest = indexedDB.open(dbName, 1);

      openRequest.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create object store if it doesn't exist
        if (!db.objectStoreNames.contains("pendingEntries")) {
          const store = db.createObjectStore("pendingEntries", {
            keyPath: "id",
          });
          store.createIndex("userId", "userId", { unique: false });
          store.createIndex("type", "type", { unique: false });
          store.createIndex("timestamp", "timestamp", { unique: false });
        }
      };

      openRequest.onsuccess = () => {
        const db = openRequest.result;
        db.close();
        console.log("[StorageCleanup] IndexedDB recreated successfully");
        resolve();
      };

      openRequest.onerror = () => {
        console.error(
          "[StorageCleanup] Failed to recreate IndexedDB:",
          openRequest.error,
        );
        reject(openRequest.error);
      };
    });
  } catch (error) {
    console.error("[StorageCleanup] IndexedDB cleanup failed:", error);
    throw error;
  }
}
/**
 * Perform full storage cleanup
 */
async function performCleanup(aggressive = false): Promise<{
  success: boolean;
  migrated: boolean;
  errors: string[];
}> {
  const errors: string[] = [];
  let migrated = false;

  // Check localStorage
  let localStorageCheck: StorageCheckResult | null = null;
  try {
    localStorageCheck = checkLocalStorage();
    if (
      !localStorageCheck.isValid ||
      localStorageCheck.needsCleanup ||
      localStorageCheck.needsMigration
    ) {
      cleanLocalStorage(true, aggressive);
      migrated = localStorageCheck.needsMigration;
    }
    if (localStorageCheck.errors.length > 0) {
      errors.push(...localStorageCheck.errors);
    }
  } catch (error) {
    console.error("[StorageCleanup] localStorage cleanup failed:", error);
    errors.push(
      `localStorage cleanup failed: ${error instanceof Error ? error.message : "Unknown error"}`,
    );
  }

  // Check IndexedDB
  let indexedDBCheck: StorageCheckResult | null = null;
  try {
    indexedDBCheck = await checkIndexedDB();
    if (
      !indexedDBCheck.isValid ||
      indexedDBCheck.needsCleanup ||
      indexedDBCheck.needsMigration
    ) {
      console.log("[StorageCleanup] IndexedDB issues detected, cleaning...");
      try {
        await cleanIndexedDB();
        migrated = indexedDBCheck.needsMigration;
      } catch (cleanError) {
        console.error("[StorageCleanup] IndexedDB cleanup failed:", cleanError);
        errors.push(
          `IndexedDB cleanup failed: ${cleanError instanceof Error ? cleanError.message : "Unknown error"}`,
        );
      }
    }
    if (indexedDBCheck.errors.length > 0) {
      errors.push(...indexedDBCheck.errors);
    }
  } catch (error) {
    console.error("[StorageCleanup] IndexedDB check failed:", error);
    errors.push(
      `IndexedDB check failed: ${error instanceof Error ? error.message : "Unknown error"}`,
    );
  }

  return {
    success: errors.length === 0,
    migrated,
    errors,
  };
}

/**
 * Initialize storage cleanup service
 * Called on app startup to detect and fix storage issues
 */
export async function initializeStorageCleanup(): Promise<void> {
  try {
    // Check current versions
    const storedStorageVersion = localStorage.getItem(STORAGE_VERSION_KEY);
    const storedAppVersion = localStorage.getItem(APP_VERSION_KEY);

    const currentStorageVersion = CURRENT_STORAGE_VERSION;
    const currentAppVersion = CURRENT_APP_VERSION;

    const storageVersionChanged =
      storedStorageVersion !== String(currentStorageVersion);
    const appVersionChanged = storedAppVersion !== currentAppVersion;

    console.log("[StorageCleanup] Initialized storage version:", currentStorageVersion);
    console.log("[StorageCleanup] Initialized app version:", currentAppVersion);

    // Perform cleanup if versions changed or first run
    if (storageVersionChanged || appVersionChanged || !storedStorageVersion) {
      const aggressive = storageVersionChanged;
      const result = await performCleanup(aggressive);

      if (result.migrated) {
        console.log("[StorageCleanup] Storage migrated successfully");
      }

      if (!result.success) {
        console.warn("[StorageCleanup] Cleanup completed with errors:", result.errors);
      }
    }

    // Update stored versions
    localStorage.setItem(STORAGE_VERSION_KEY, String(currentStorageVersion));
    localStorage.setItem(APP_VERSION_KEY, currentAppVersion);
  } catch (error) {
    console.error("[StorageCleanup] Failed to initialize storage cleanup:", error);
    // Don't throw - storage cleanup failure should not block app startup
  }
}

/**
 * Initialize storage version keys (called on first run)
 * Exported for main.tsx compatibility.
 */
export function initializeStorageVersion(): void {
  try {
    const storedVersion = localStorage.getItem(STORAGE_VERSION_KEY);
    if (!storedVersion) {
      localStorage.setItem(STORAGE_VERSION_KEY, String(CURRENT_STORAGE_VERSION));
    }
    const storedAppVersion = localStorage.getItem(APP_VERSION_KEY);
    if (!storedAppVersion) {
      localStorage.setItem(APP_VERSION_KEY, CURRENT_APP_VERSION);
    }
  } catch {
    // localStorage unavailable — not critical
  }
}

/**
 * Perform storage cleanup and return result.
 * Exported for main.tsx compatibility.
 */
export async function performStorageCleanup(): Promise<{
  cleaned: boolean;
  migrated: boolean;
  errors: string[];
}> {
  try {
    const storedStorageVersion = localStorage.getItem(STORAGE_VERSION_KEY);
    const storedAppVersion = localStorage.getItem(APP_VERSION_KEY);
    const storageVersionChanged = storedStorageVersion !== String(CURRENT_STORAGE_VERSION);
    const appVersionChanged = storedAppVersion !== CURRENT_APP_VERSION;
    const aggressive = storageVersionChanged;

    if (storageVersionChanged || appVersionChanged || !storedStorageVersion) {
      const result = await performCleanup(aggressive);
      localStorage.setItem(STORAGE_VERSION_KEY, String(CURRENT_STORAGE_VERSION));
      localStorage.setItem(APP_VERSION_KEY, CURRENT_APP_VERSION);
      return {
        cleaned: result.success,
        migrated: result.migrated,
        errors: result.errors,
      };
    }

    return { cleaned: false, migrated: false, errors: [] };
  } catch (error) {
    console.error("[StorageCleanup] performStorageCleanup failed:", error);
    return {
      cleaned: false,
      migrated: false,
      errors: [error instanceof Error ? error.message : "Unknown error"],
    };
  }
}
